import type { Projection, ResolutionStatus } from '@v2/sdk';

export const PROTOCOL_VERSION = 2;
export type MemberKind = 'human' | 'bot';
export type RoomPhase = 'lobby' | 'playing' | 'complete';

/** A game the host can run. Players are bound to seats when the leader starts. */
export interface GameListing {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly players: { readonly min: number; readonly max: number };
}
/** Someone in the room. Members join in the lobby and keep their place across reconnects. */
export interface RoomMember {
  readonly id: string;
  readonly name: string;
  readonly kind: MemberKind;
  readonly leader: boolean;
  readonly connected: boolean;
  /** Seat in the current game, once one has started. */
  readonly playerId: string | null;
}
export interface BotStatus {
  readonly state: 'idle' | 'thinking' | 'waiting' | 'paused';
  /** Wall-clock time when a bounded provider cooldown will resume automatically. */
  readonly retryAt?: number;
  readonly playerId?: string;
  readonly message?: string;
  readonly decisions: number;
  readonly decisionLimit: number;
  readonly totalTokens: number;
}
/** Only the requesting member's projection crosses the network. */
export interface HistoryRequest {
  readonly id: string;
  readonly requester: string;
  readonly revision: number;
  readonly approved: readonly string[];
  readonly required: readonly string[];
}
export interface RoomHistory {
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly request?: HistoryRequest;
  readonly autoApprove: readonly string[];
}
export interface RoomView {
  readonly history?: RoomHistory;
  readonly roomId: string;
  readonly code: string;
  readonly inviteUrl: string;
  readonly phase: RoomPhase;
  readonly game: GameListing | null;
  readonly members: readonly RoomMember[];
  readonly you: { readonly memberId: string; readonly playerId: string | null; readonly leader: boolean } | null;
  readonly takeover?: string;
  readonly projection?: Projection;
  readonly resolution?: ResolutionStatus;
  readonly bot: BotStatus;
  readonly generation: number;
  /** Orders lobby, bot and viewer changes even when no rules command occurs. */
  readonly version: number;
}
/** What the home page knows about a room you belong to. */
export interface RoomSummary {
  readonly roomId: string;
  readonly code: string;
  readonly phase: RoomPhase;
  readonly gameName: string | null;
  readonly leaderName: string;
  readonly memberCount: number;
  readonly lastActiveAt: number;
}
export interface RoomCreated { readonly roomId: string; readonly code: string }
export interface RoomCommand { readonly id: string; readonly expectedRevision: number; readonly actionId: string; /** Chosen values for a set-controlled action. */ readonly values?: readonly string[] }
export interface CommandReceipt { readonly id: string; readonly revision: number }
export interface RoomError { readonly error: string; readonly code: string }
export interface TablePing { readonly position: readonly [number, number, number]; readonly generation: number }
export function validTablePing(value: unknown): value is TablePing {
  if (!value || typeof value !== 'object') return false;
  const ping = value as TablePing;
  return Number.isSafeInteger(ping.generation) && Array.isArray(ping.position) && ping.position.length === 3 && ping.position.every(n => typeof n === 'number' && Number.isFinite(n) && Math.abs(n) <= 1000);
}
export type RoomEvent = { readonly type: 'attention'; readonly ping: TablePing } | { readonly type: 'room'; readonly room: RoomView } | { readonly type: 'error'; readonly error: string; readonly code: string };

export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const ROOM_CODE_LENGTH = 6;
export function normalizeRoomCode(value: string): string | null {
  const code = value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/O/g, '0').replace(/I/g, '1').replace(/L/g, '1');
  return code.length === ROOM_CODE_LENGTH && [...code].every(char => ROOM_CODE_ALPHABET.includes(char)) ? code : null;
}
export const GUEST_ID_PATTERN = /^guest_[A-Za-z0-9_-]{22,43}$/;
